import { alpha, Divider } from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { Reference as ProductionReference } from 'sr-types/lib/production/v1/graphql';
import { Note } from 'sr-types/lib/search/v1/graphql';
import { COLOR_STATUS_NOTE, COLOR_STATUS_NOTE_DARK } from '../../../common/Colors';
import { V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import ProductionStatusNoteHeader from './ProductionStatusNoteHeader';
import ProductionStatusNoteRenderer from './ProductionStatusNoteRenderer';

type ProductionStatusNoteDisplayProps = {
    note: Note;
    productionReference: ProductionReference;
    onStatusNoteDeleted: (noteId: string, productionId: string) => void;
    onStatusNoteChanged?: (note: Note | null, productionId: string) => void;
    setExpandNoteSection?: Dispatch<SetStateAction<boolean>>;
    onNewStatusNoteClick?: ({ isCreateNewNote }: { isCreateNewNote: boolean }) => void;
};

export default function ProductionStatusNoteDisplay({
    note,
    productionReference,
    onStatusNoteDeleted,
    onStatusNoteChanged,
    setExpandNoteSection,
    onNewStatusNoteClick
}: ProductionStatusNoteDisplayProps) {
    const { theme, isDarkMode } = useStyling();

    const [isEditing, setIsEditing] = useState<boolean>(false);

    const toggleEditor = (state: boolean) => {
        setIsEditing(state);
        setExpandNoteSection?.(state);
    };

    return (
        <V
            sx={{
                px: 1.25,
                py: 1,
                gap: 0.5,
                border: `1px solid ${isDarkMode ? COLOR_STATUS_NOTE_DARK : COLOR_STATUS_NOTE}`,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                maxHeight: 215,
                minHeight: 100,
                flex: 3
            }}
        >
            {!isEditing && (
                <>
                    <ProductionStatusNoteHeader
                        toggleEditor={toggleEditor}
                        currentNoteId={note.id}
                        onStatusNoteDeleted={onStatusNoteDeleted}
                        productionId={productionReference.id}
                        onNewStatusNoteClick={onNewStatusNoteClick}
                    />
                    <Divider
                        sx={{
                            width: '100%',
                            backgroundColor: alpha(
                                isDarkMode ? theme.palette.common.white : theme.palette.common.black,
                                0.12
                            )
                        }}
                    />
                </>
            )}

            <ProductionStatusNoteRenderer
                key={note.id}
                note={note}
                isEditing={isEditing}
                toggleEditor={toggleEditor}
                productionReference={productionReference}
                onStatusNoteChanged={onStatusNoteChanged}
            />
        </V>
    );
}
